import type { StorageService } from '@/services/storage/types';

import { AttributionCollector } from './AttributionCollector';
import { AttributionStore } from './AttributionStore';
import {
  DEFAULT_ATTRIBUTION_CONFIG,
  type AttributionConfig,
  type AttributionIdentifiers,
  type AttributionService,
} from './types';

export class AttributionServiceImpl implements AttributionService {
  private readonly store: AttributionStore;

  private readonly collector: AttributionCollector;

  private readonly enabled: boolean;

  private snapshot: AttributionIdentifiers = {};

  private active = false;

  constructor(
    storage: StorageService,
    config: AttributionConfig = DEFAULT_ATTRIBUTION_CONFIG
  ) {
    this.enabled = config.enabled !== false;
    this.store = new AttributionStore(storage, {
      persist: config.persist !== false,
      ttlDays: config.ttlDays ?? 90,
    });
    this.collector = new AttributionCollector({
      customParams: config.customClickIdParams ?? [],
      captureUrlParams: config.captureUrlParams !== false,
      captureCookies: config.captureCookies !== false,
    });
  }

  init(): void {
    if (this.active) return;
    this.active = true;

    if (!this.enabled) return;

    const stored = this.store.load();
    if (stored) {
      this.snapshot = stored;
    }

    this.collect();
  }

  destroy(): void {
    if (!this.active) return;
    this.active = false;
  }

  collect(): AttributionIdentifiers {
    if (!this.enabled) return this.snapshot;

    const fresh = this.collector.collectFresh();
    this.snapshot = this.collector.merge(this.snapshot, fresh);
    this.store.save(this.snapshot);
    return this.snapshot;
  }

  getSnapshot(): AttributionIdentifiers {
    return { ...this.snapshot };
  }

  update(partial: Partial<AttributionIdentifiers>): AttributionIdentifiers {
    this.snapshot = this.collector.merge(this.snapshot, partial);
    this.store.save(this.snapshot);
    return this.getSnapshot();
  }

  clear(): void {
    this.snapshot = {};
    this.store.clear();
  }
}
